import { createContext, useContext, useMemo } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import Polyglot from "node-polyglot";
import { formatPrice, formatValue } from "asos-web-currency-formatter";
import {
  buildUrl,
  getLocale,
  getStoreCode,
  getStoreUrl,
  getCurrencyCode
} from "../../state/modules/config/selectors";

export const buildNumberFormatterFn = locale => number => {
  const formatAsInteger = true;

  return formatValue(number, locale, formatAsInteger);
};

export const buildTranslationFormatterFn = (locale, phrases) => {
  const polyglot = new Polyglot({
    locale,
    phrases
  });

  return (...args) => polyglot.t(...args);
};

export const buildCurrencyFormatterFn = (locale, currency) => (
  value,
  formatAsInteger
) => formatPrice(value, locale, currency, formatAsInteger);

export const LocaleContext = createContext(null);

export const useLocale = () => {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error("useLocale must be used within a LocaleProvider");
  }

  return context;
};

export const LocaleProvider = ({
  locale,
  storeCode,
  storeUrl,
  currency,
  translations,
  children,
  buildTranslationFormatter = buildTranslationFormatterFn,
  buildNumberFormatter = buildNumberFormatterFn,
  buildCurrencyFormatter = buildCurrencyFormatterFn
}) => {
  const value = useMemo(
    () => ({
      formatLink: buildUrl.bind(null, { storeUrl, storeCode }),
      formatTranslation: buildTranslationFormatter(locale, translations),
      formatNumber: buildNumberFormatter(locale),
      formatCurrency: buildCurrencyFormatter(locale, currency)
    }),
    [
      locale,
      storeCode,
      storeUrl,
      currency,
      translations,
      buildTranslationFormatter,
      buildNumberFormatter,
      buildCurrencyFormatter
    ]
  );

  return (
    <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
  );
};

LocaleProvider.propTypes = {
  locale: PropTypes.string.isRequired,
  storeCode: PropTypes.string.isRequired,
  storeUrl: PropTypes.string.isRequired,
  currency: PropTypes.string.isRequired,
  translations: PropTypes.object.isRequired,
  children: PropTypes.node.isRequired,
  buildTranslationFormatter: PropTypes.func,
  buildNumberFormatter: PropTypes.func,
  buildCurrencyFormatter: PropTypes.func
};

const ConnectedLocaleProvider = ({ translations, children, ...rest }) => {
  const locale = useSelector(getLocale);
  const storeCode = useSelector(getStoreCode);
  const currency = useSelector(getCurrencyCode);
  const storeUrl = useSelector(getStoreUrl);

  return (
    <LocaleProvider
      locale={locale}
      storeCode={storeCode}
      currency={currency}
      storeUrl={storeUrl}
      translations={translations}
      {...rest}
    >
      {children}
    </LocaleProvider>
  );
};

ConnectedLocaleProvider.propTypes = {
  translations: PropTypes.object.isRequired,
  children: PropTypes.node.isRequired
};

export default ConnectedLocaleProvider;
